import * as ko from 'knockout';

import { DimensionMeta } from './dimension_meta';
import { Dimension } from './dimension';
import { CountryData } from '../api/countries';
import { RegionData } from '../api/regions';
import { SiteData } from '../api/sites';
import { session } from '../session';
import { AgroRegionData } from '../api/agro_regions';
import { NameData } from '../api/names';
import { GeoJSON } from '../api/datasets';
import { Point } from '../ko_bindings/map';
import { asTurfFeature, asTurfPolygon } from '../geo';
import pointOnFeature from '@turf/point-on-feature';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import distance from '@turf/distance';
import i18n from '../i18n';
import { weatherEnabled } from '../permissions';

export class Site extends Dimension {
  LOCATION_WARNING_DISTANCE_THRESHOLD_KM = 5;
  country = ko.observable<CountryData>(null).extend({
    serverError: true,
  });
  region = ko.observable<RegionData>(null).extend({
    serverError: true,
  });
  agroRegion = ko.observable<AgroRegionData>(null).extend({
    serverError: true,
  });
  partner = ko.observable<NameData>(null);
  customer = ko.observable<NameData>(null);
  siteArea = ko.observable<GeoJSON>(null);
  gpsLocation = ko.observable<Point>();
  autoFetchWeather = ko.observable<boolean>(true);
  wasMapModalClosed = ko.observable(false);
  wasGpsLocationWarningClosed = ko.observable(false);
  gpsLocationPointBasedOnSiteArea = ko.pureComputed<Point | null>(() => {
    const siteAreaFeature = asTurfFeature(this.siteArea());
    if (siteAreaFeature) {
      const point = pointOnFeature(siteAreaFeature);
      return {
        lat: point.geometry.coordinates[1],
        lng: point.geometry.coordinates[0],
      };
    }
    return null;
  }, this);

  shouldShowLocationWarning = ko.pureComputed(() => {
    const siteAreaPolygon = asTurfPolygon(this.siteArea());
    const gpsLocation = this.gpsLocation();
    if (!siteAreaPolygon || !gpsLocation) {
      return false;
    }
    // The ring is closed here, so its last position repeats its first. Measuring that
    // duplicate twice does not change whether every position is beyond the threshold.
    const outerRing = siteAreaPolygon.geometry.coordinates[0];
    return (
      outerRing.every((position) => {
        return (
          distance([gpsLocation.lng, gpsLocation.lat], [position[0], position[1]], {
            units: 'kilometers',
          }) > this.LOCATION_WARNING_DISTANCE_THRESHOLD_KM
        );
      }) && !booleanPointInPolygon([gpsLocation.lng, gpsLocation.lat], siteAreaPolygon)
    );
  });
  editUrl = ko.pureComputed(() => {
    return '/sites/' + this.id() + '/';
  });
  constructor(data?: SiteData, dimensionMeta?: DimensionMeta) {
    super(data, dimensionMeta);

    let tenant = session.tenant();
    if (tenant?.country_mandatory_for_site) {
      this.country = this.country.extend({ required: true });
    }
    if (tenant?.region_mandatory_for_site) {
      this.region = this.region.extend({ required: true });
    }
    if (tenant?.agro_region_mandatory_for_site) {
      this.agroRegion = this.agroRegion.extend({ required: true });
    }

    this.gpsLocation = this.gpsLocation.extend({
      required: {
        onlyIf: () => weatherEnabled() && this.autoFetchWeather(),
        message: i18n.t('GPS location is required when automatic weather fetching is enabled.')(),
      },
    });

    if (data) {
      this.country(data.country);
      this.region(data.region);
      this.agroRegion(data.agro_region);
      this.partner(data.partner);
      this.customer(data.customer);
      this.siteArea(data.site_area);
      this.gpsLocation(data.gps_location);
      this.autoFetchWeather(data.auto_fetch_weather);
    }
    this.gpsLocationPointBasedOnSiteArea.subscribe((pointBasedOnSiteArea) => {
      if (pointBasedOnSiteArea) {
        this.gpsLocation(pointBasedOnSiteArea);
      }
    });
    this.gpsLocation.subscribe(() => {
      this.wasMapModalClosed(false);
      this.wasGpsLocationWarningClosed(false);
    });
    this.siteArea.subscribe(() => {
      this.wasMapModalClosed(false);
      this.wasGpsLocationWarningClosed(false);
    });
  }

  toData(): SiteData {
    return {
      country: this.country(),
      region: this.region(),
      agro_region: this.agroRegion(),
      partner: this.partner(),
      customer: this.customer(),
      site_area: this.siteArea(),
      gps_location: this.gpsLocation(),
      auto_fetch_weather: this.autoFetchWeather(),
      ...super.toData(),
    };
  }
}
